/**
 * AI Chat Panel
 *
 * iMessage-style chat drawer for conversational AI assistance on content.
 * Ephemeral messages (not persisted). Uses aiApi.execute() for direct prompts.
 */

import { useState, useRef, useEffect } from 'react';
import { Drawer, Input, Button, Space, Typography, message as antMessage } from 'antd';
import { SendOutlined, CopyOutlined, MessageOutlined } from '@ant-design/icons';
import { aiApi } from '@api/ai';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatPanelProps {
  open: boolean;
  onClose: () => void;
  contextContent: string;
  onPasteToField?: (content: string) => void;
}

export default function AIChatPanel({
  open,
  onClose,
  contextContent,
  onPasteToField,
}: AIChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset messages when drawer closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInput('');
    }
  }, [open]);

  const sendMessage = async (text?: string) => {
    const userText = text || input.trim();
    if (!userText) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: userText,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const systemMessage = `You are a helpful assistant. The user is editing the following content:\n\n---\n${contextContent}\n---\n\nKeep responses short and concise. If the user asks you to rewrite or modify content, return only the modified text.`;

      const result = await aiApi.execute({
        prompt: userText,
        systemMessage,
        maxTokens: 1000,
      });

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: result.content || 'No response received.',
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      antMessage.error('AI request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <MessageOutlined />
          AI Chat
        </Space>
      }
      open={open}
      onClose={onClose}
      width={380}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          height: '100%',
        },
      }}
    >
      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            <Text type="secondary">Ask AI about your content</Text>
            <div style={{ marginTop: 12 }}>
              <Space direction="vertical" size={4}>
                {['Improve the tone', 'Make it more concise', 'Add more detail'].map((suggestion) => (
                  <Button
                    key={suggestion}
                    size="small"
                    type="dashed"
                    onClick={() => sendMessage(suggestion)}
                    style={{ fontSize: 12 }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Space>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: msg.role === 'user'
                  ? 'var(--ant-color-primary, #1677ff)'
                  : 'var(--ant-color-bg-container-disabled, #f0f0f0)',
                color: msg.role === 'user' ? '#fff' : 'inherit',
              }}
            >
              <Paragraph
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontSize: 13,
                  color: msg.role === 'user' ? '#fff' : 'inherit',
                }}
              >
                {msg.content}
              </Paragraph>
              {msg.role === 'assistant' && onPasteToField && (
                <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    style={{ fontSize: 11, padding: '0 4px', height: 'auto' }}
                    onClick={() => {
                      onPasteToField(msg.content);
                      antMessage.success('Pasted to field');
                    }}
                  >
                    Paste to field
                  </Button>
                  <Button
                    size="small"
                    type="text"
                    style={{ fontSize: 11, padding: '0 4px', height: 'auto' }}
                    onClick={() => sendMessage('Tell me more')}
                  >
                    Tell me more
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: '16px 16px 16px 4px',
                background: 'var(--ant-color-bg-container-disabled, #f0f0f0)',
              }}
            >
              <Text type="secondary" style={{ fontSize: 13 }}>Thinking...</Text>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
        }}
      >
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask AI..."
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={isLoading}
            style={{ borderRadius: '8px 0 0 8px' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => sendMessage()}
            loading={isLoading}
            disabled={!input.trim()}
            style={{ borderRadius: '0 8px 8px 0', height: 'auto' }}
          />
        </Space.Compact>
      </div>
    </Drawer>
  );
}
